import { modbusObjectTypes, modbusValueTypes } from "../enums";
import {
  DataType,
  MODBUS_DATA_TYPE_CODES,
  MODBUS_UI_TYPES,
  ParamType,
  RegType,
  RW,
} from "../types";

export type Option = { value: string | number; label: string; disabled?: boolean };

const toOptions = (values: readonly (string | number)[]): Option[] =>
  values.map((v) => ({ value: v, label: String(v) }));

/** String-domain register types (legacy device-mapping / template editors). */
export const REG_TYPES: RegType[] = ["Holding (4x)", "Input (3x)", "Coil (0x)", "Discrete (1x)"];
export const DATA_TYPES: DataType[] = ["INT16", "INT16(U)", "FLOAT32", "FLOAT32(U)", "INT32", "INT32(U)", "BOOL"];
export const PARAM_TYPES: ParamType[] = ["BU", "BO", "AI", "AO", "MI", "MO"];
export const RW_OPTIONS: RW[] = ["R", "R/W", "W"];

export const REG_TYPE_OPTIONS = toOptions(REG_TYPES);
export const DATA_TYPE_OPTIONS = toOptions(DATA_TYPES);
export const PARAM_TYPE_OPTIONS = toOptions(PARAM_TYPES);
export const RW_SELECT_OPTIONS = toOptions(RW_OPTIONS);

/** Read-only boolean rendered as an R / R/W choice (`true` → "R"). */
export const RW_BOOL_OPTIONS: Option[] = [
  { value: "R", label: "R" },
  { value: "R/W", label: "R/W" },
];

/** Numeric-domain register/data types (interface templates) sourced from enums. */
export const REG_TYPE_NUMERIC_OPTIONS: Option[] = Object.entries(modbusObjectTypes).map(([v, label]) => ({
  value: Number(v),
  label,
}));
export const DATA_TYPE_NUMERIC_OPTIONS: Option[] = Object.entries(modbusValueTypes).map(([v, label]) => ({
  value: Number(v),
  label,
}));

export const UNIVERSAL_POINTS = ["On/Off", "Setpoint", "RoomTemp", "Mode", "Swing", "Error", "Fan Speed"];

export const isEnumType = (type: ParamType) => type === "MI" || type === "MO";

/* ---- API-aligned option sets (functional / device templates) ---- */

/** uiType numeric options (BACnet template-type enum). */
export const UI_TYPE_OPTIONS: Option[] = Object.entries(MODBUS_UI_TYPES).map(([v, label]) => ({
  value: Number(v),
  label,
}));

/** dataType string-code options ("s16" → "INT16", …). */
export const DATA_TYPE_CODE_OPTIONS: Option[] = Object.entries(MODBUS_DATA_TYPE_CODES).map(([v, label]) => ({
  value: v,
  label: `${label} (${v})`,
}));

export const REGISTER_TYPE_COIL = 0;
export const REGISTER_TYPE_DISCRETE_INPUT = 1;
export const REGISTER_TYPE_INPUT = 3;
export const REGISTER_TYPE_HOLDING = 2;

export const isBitPackableRegisterType = (registerType: number | null | undefined): boolean =>
  registerType === REGISTER_TYPE_HOLDING || registerType === REGISTER_TYPE_INPUT;

export const BIT_NUMBER_OPTIONS: Option[] = Array.from({ length: 16 }, (_, i) => ({
  value: i,
  label: String(i),
}));

/**
 * Engineering-unit options. Stored on the row as the lowercase `value`
 * ("kw"), displayed as the cased `label` ("kW").
 */
export const ENG_UNIT_OPTIONS: Option[] = [
  { value: "kw", label: "kW" },
  { value: "kwh", label: "kWh" },
  { value: "v", label: "V" },
  { value: "a", label: "A" },
  { value: "c", label: "°C" },
  { value: "f", label: "°F" },
  { value: "pct", label: "%" },
  { value: "rh", label: "%R.H" },
  { value: "pa", label: "Pa" },
  { value: "kpa", label: "kPa" },
  { value: "psi", label: "PSI" },
  { value: "bar", label: "Bar" },
  { value: "kgcm2", label: "kg/cm²" },
  { value: "inwc", label: "inWC" },
  { value: "mmhg", label: "mmHg" },
  { value: "hz", label: "Hz" },
  { value: "cfm", label: "CFM" },
  { value: "ls", label: "L/s" },
  { value: "fpm", label: "FPM" },
  { value: "gpm", label: "GPM" },
  { value: "btuh", label: "BTU/h" },
];

/**
 * Resolves a stored eng-unit value to its display label ("c" -> "°C").
 * Matching is case-insensitive and also accepts a stored label, so rows saved
 * as "C" or "°C" still resolve. Unknown/free-text units pass through as-is.
 */
export const engUnitLabel = (units?: string | null): string => {
  if (!units) return "";
  const key = String(units).trim().toLowerCase();
  const match = ENG_UNIT_OPTIONS.find(
    (o) => String(o.value).toLowerCase() === key || String(o.label).toLowerCase() === key,
  );
  return match ? String(match.label) : units;
};
